import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Modifier } from 'react-day-picker-old/types/common';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	accountHasComplianceEnabled,
	getComplianceDisabledDays,
	getScheduleTimeOptions,
	getSendOnBehalfDisabledDays,
	getSendOnBehalfWaitTime,
} from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { bs } from '../../../styles/styles';
import { CampaignDayPicker } from '../../CampaignDayPicker';
import { Separator } from '../../Separator';
import { styleSheet } from './styles';

export const CampaignDateTimePicker = inject(ImpersonationContextKey)(
	observer(function CampaignDateTimePicker({
		initialValue,
		onSave,
		onCancel,
		expirationDate,
		impersonationContext,
		sendFrom,
		sendFromUserId,
		ctaText,
		ignoreDisabledDays = false,
		ignoreEmailTimeIntervals = false,
	}: {
		initialValue: Date;
		onSave(newValue: Date): void;
		onCancel(): void;
		expirationDate?: Date;
		sendFrom?: Api.ISendEmailFrom;
		sendFromUserId?: string;
		ctaText?: React.ReactNode;
		// Ultimately would probably make sense to have a social version of this whole component with email aspects taken out.
		// Meantime while social still uses this component, the ignore props below can be used to opt-out of some email-specific date restrictions.
		ignoreDisabledDays?: boolean;
		ignoreEmailTimeIntervals?: boolean;
	} & IImpersonationContextComponentProps) {
		const userSession = useUserSession();
		const [value, setValue] = React.useState<Date>(initialValue);
		const [selectedTimeMinutes, setSelectedTimeMinutes] = React.useState<number>(
			initialValue ? initialValue.getHours() * 60 + initialValue.getMinutes() : null
		);
		const [inputValue, setInputValue] = React.useState('');

		const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);

		const minStartDate = React.useMemo(() => {
			if (!sendFrom) {
				return new Date();
			}
			const currentUserId = impersonationContext?.isValid ? impersonationContext?.user?.id : userSession.user.id;
			const senderIsNotCurrentUser =
				Api.SendEmailFrom.ContactOwner === sendFrom ||
				(Api.SendEmailFrom.SelectedUser === sendFrom && sendFromUserId !== currentUserId);
			return requiresCompliance && senderIsNotCurrentUser
				? moment
						.max([
							moment(getComplianceDisabledDays(userSession, impersonationContext, false)),
							moment(getSendOnBehalfWaitTime(userSession, impersonationContext)),
						])
						.toDate()
				: requiresCompliance
					? getComplianceDisabledDays(userSession, impersonationContext, false)
					: senderIsNotCurrentUser
						? getSendOnBehalfDisabledDays(userSession, impersonationContext)
						: null;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const onTimeChanged = (totalMinutes: number) => {
			setSelectedTimeMinutes(totalMinutes);
			const hours = Math.floor(totalMinutes / 60);
			const mins = totalMinutes % 60;
			value.setHours(hours, mins, 0);
			setValue(value);
		};

		const timeOptions = getScheduleTimeOptions(
			0,
			userSession,
			undefined,
			moment(value),
			impersonationContext,
			ignoreEmailTimeIntervals,
			15,
			true
		).map(o => ({
			label: o.title as string,
			value: o.value,
		}));
		const NUM_WEEKS_SHOWN_IN_CAL = 6;

		const disabledDays = expirationDate
			? [{ after: expirationDate, before: minStartDate ? minStartDate : new Date() }]
			: [{ before: minStartDate ? minStartDate : new Date() }];

		const businessHours =
			impersonationContext?.account?.features?.sendEmail || userSession?.account?.features?.sendEmail;

		const outsideBusinessHourDays: Modifier[] = [];
		if (businessHours?.observeSendIntervals && ignoreDisabledDays === false) {
			if (!businessHours?.saturdayInterval?.startMinutes && !businessHours?.saturdayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [6] });
			}
			if (!businessHours?.sundayInterval?.startMinutes && !businessHours?.sundayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [0] });
			}
			if (!businessHours?.fridayInterval?.startMinutes && !businessHours?.fridayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [5] });
			}
		}

		const onDaySelected = (day: Date) => {
			day.setHours(value.getHours(), value.getMinutes());
			setValue(day);
		};

		return (
			<div className={css(styleSheet.DateTimePicker)}>
				<div className={css(styleSheet.body)}>
					<CampaignDayPicker
						selectedDay={value}
						disabledDays={[...disabledDays, ...outsideBusinessHourDays]}
						numWeeksShown={NUM_WEEKS_SHOWN_IN_CAL}
						onDayClick={onDaySelected}
						modifiers={null}
						styles={[bs.m0, bs.wFull]}
					/>
					<Separator />
					<Combobox
						items={timeOptions}
						value={timeOptions.find(o => o.value === selectedTimeMinutes) ?? null}
						onValueChange={item => {
							const timeItem = item as { label: string; value: number } | null;
							if (timeItem) {
								onTimeChanged(timeItem.value);
							}
						}}
						inputValue={inputValue}
						onInputValueChange={setInputValue}
						itemToStringLabel={item => (item as { label: string }).label}
						isItemEqualToValue={(a, b) => (a as { value: number }).value === (b as { value: number }).value}
					>
						<ComboboxInput placeholder='Search time...' styles={[bs.w40]} />
						<ComboboxContent>
							<ComboboxEmpty>No matching times</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: number }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
					<Separator />
				</div>
				<div className={css(styleSheet.footer)}>
					<Button onClick={() => onSave(value)}>{ctaText ? ctaText : 'Schedule'}</Button>
					<Button variant='secondary' onClick={onCancel}>
						Cancel
					</Button>
				</div>
			</div>
		);
	})
);
